import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '../stores/authStore'
import { api, apiDelete, apiPost, apiPut } from '../lib/api'
import { InlineError } from '../components/InlineError'
import { ConfirmDialog } from '../components/ConfirmDialog'
import ShortcutPanel from '../components/boss/ShortcutPanel'
import type { ShortcutOrderItem } from '../components/boss/ShortcutPanel'
import ReportPicker from '../components/boss/ReportPicker'
import type { ReportPickerTab } from '../components/boss/ReportPicker'
import PreFilterDialog from '../components/boss/PreFilterDialog'
import FieldBar from '../components/boss/FieldBar'
import GridMenu from '../components/boss/GridMenu'
import SqlDebugDialog from '../components/boss/SqlDebugDialog'
import BossPanel from '../components/boss/BossPanel'
import { useBossPanels } from '../lib/bossPanels'
import {
    decodePreFilters, defaultsFromFields, describePreFilter, encodePreFilters, unresolvedPrompts,
} from '../lib/bossPreFilters'
import {
    formatOf, inventory, isGrouped, moveField, rowLayoutOf, setAggregate, setFormat, setRowLayout,
    setSortBySummary, withDefaultRowLayout,
} from '../lib/bossLayoutOps'
import type { BossAggregate, BossFieldArea, BossFormat, BossRowLayout } from '../lib/bossLayoutOps'
import {
    isRowFieldPinned, normaliseRowPins, pinRowField, rowPinsOfEnvelope, unpinRowField,
} from '../lib/bossRowPins'
import {
    collapseOfEnvelope, hiddenTotalsOfEnvelope, normaliseHiddenTotals, normaliseRowCollapse, sameHiddenTotals,
} from '../lib/bossRowCollapse'
import type { BossRowPins } from '../lib/bossRowPins'
// Both of these must stay TYPE-only imports. PivotGrid and the viewer adapter reach
// @perspective-dev, which is ~3.8 MB of WASM behind the lazy boundary below; a value import from
// either would pull the whole thing into the main bundle and undo it.
import type { BossGridTarget } from '../components/boss/PivotGrid'
import type { BossViewerHandle } from '../lib/perspectiveViewer'
import type {
    BossDeleteImpact,
    BossField, BossLayout, BossLegacyImportResult, BossLegacyList, BossPreFilterValue,
    BossReportDetail, BossReportList, BossRunRequest, BossRunResult, BossShortcutBar,
    BossShortcutGroup, BossSqlPreview, BossStorePicker, BossSupplierPicker,
} from '../types/boss'
import '../styles/ui.css'
import '../styles/boss-reports.css'

// Perspective ships ~3.8 MB of WASM. Behind a lazy boundary it is fetched only once a report
// has actually returned rows — never on another route, and never on this one while the user is
// still choosing a report.
const PivotGrid = lazy(() => import('../components/boss/PivotGrid'))

// ── Presentation helpers ──────────────────────────────────────────────

/**
 * The api() helper rethrows the raw response body, which for this feature is either
 * `{ "message": … }` (4xx) or an RFC 9110 ProblemDetails document (502). Both are unwrapped to
 * the one sentence worth showing; a 502's sentence carries the correlation id and nothing else.
 */
function errorMessage(err: unknown): string {
    const raw = (err as Error)?.message ?? ''
    try {
        const parsed = JSON.parse(raw)
        return parsed.detail || parsed.message || parsed.error || raw
    } catch {
        return raw || 'The report could not be loaded.'
    }
}

const countFmt = new Intl.NumberFormat('en-CA')

const clockFmt = new Intl.DateTimeFormat('en-CA', { hour: 'numeric', minute: '2-digit' })
const stampFmt = new Intl.DateTimeFormat('en-CA', { dateStyle: 'medium', timeStyle: 'medium' })

/** The wall-clock time a run's figures came out of the database, in the reader's own zone. */
function asOfClock(ranAt: string): string {
    const moment = new Date(ranAt)
    return Number.isNaN(moment.getTime()) ? '—' : clockFmt.format(moment)
}

/** The same moment in full, for the tooltip — a bare clock time is ambiguous after midnight. */
function asOfTitle(ranAt: string): string {
    const moment = new Date(ranAt)
    if (Number.isNaN(moment.getTime())) return 'This run has no recorded time.'
    return `These figures were read from the database at ${stampFmt.format(moment)}. `
        + 'Run again re-reads them.'
}

/** URL keys that carry pre-filter state (§4.3), so switching reports can clear all of them. */
const isFilterKey = (key: string) => key.startsWith('f_') || key.startsWith('d_')

/**
 * Marks a URL as deliberately carrying no pre-filters, which is not the same as a URL that was
 * never given any. Without it, removing the last filter reads as "untouched" and the report's
 * stored defaults come back — so the value the user just deleted reappears.
 */
const NO_FILTERS_KEY = 'nofilters'

/**
 * The flat Datagrid layout a report falls back to when it has no saved one. Mirrors the
 * server-side default (contract §7.5) so a report looks the same before and after its first save:
 * ratios are weighted by their denominator, which is what makes MARGIN/ROI aggregate correctly
 * at every level instead of averaging per-row ratios.
 */
function defaultViewerConfig(result: BossRunResult): Record<string, unknown> {
    const expressions: Record<string, string> = {}
    const aggregates: Record<string, unknown> = {}
    for (const d of result.derived) {
        expressions[d.name] = d.kind === 'margin'
            ? `("${d.denominator}" - "${d.numerator}") / "${d.denominator}"`
            : `"${d.numerator}" / "${d.denominator}"`
        aggregates[d.name] = ['weighted mean', [d.denominator]]
    }
    const columns = [...result.columns]
    for (const d of result.derived) if (!columns.includes(d.name)) columns.push(d.name)
    return {
        plugin: 'Datagrid',
        columns,
        group_by: [],
        split_by: [],
        aggregates,
        expressions,
        filter: [],
        sort: [],
        columns_config: {},
        // Side by side, matching the server's builders and the legacy grid: one row-header column
        // per field rather than one indented tree. Nothing is grouped yet in this fallback, so it
        // only shows once a field is dragged into Rows — which is exactly when it matters. The grid
        // menu's Row Layout switches it. (split_rollup_mode is left alone: Perspective's split
        // columns have no equivalent choice.)
        group_rollup_mode: 'flat',
        settings: false,
    }
}

/**
 * The report write DTO takes `yyyy-MM-dd` (§3.1). The codec always hands back a full ISO date —
 * a month descriptor decodes to the first of that month, a year to its Jan 1 — so anything else
 * is a value the server would reject, and is dropped rather than persisted as a bad bound.
 */
function anchorDate(value: string | undefined): string | null {
    return value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : null
}

// ── Shortcut mutations ────────────────────────────────────────────────

type ShortcutAction =
    | { kind: 'addGroup'; name: string }
    | { kind: 'renameGroup'; groupId: string; name: string }
    | { kind: 'deleteGroup'; groupId: string }
    | { kind: 'pin'; groupId: string; reportId: string }
    | { kind: 'unpin'; shortcutId: string }
    | { kind: 'reorder'; shortcuts: ShortcutOrderItem[] }

// ── Page ──────────────────────────────────────────────────────────────

/**
 * BOSS Reports — the legacy WinForms pivot-grid reporting tool, ported.
 *
 * All view state lives in the query string (`?report=`, `?prompt=`, `f_*`, `d_*`, `?store=`), so a
 * configured report is a link somebody can send. The page composes the shortcut panel, the report
 * picker, the pre-filter dialog and the Perspective grid; every query it owns is keyed exactly as
 * docs/BossReportsContract.md §4.2 specifies.
 */
export default function BossReportsPage() {
    const token = useAuthStore(s => s.accessToken)
    const user = useAuthStore(s => s.user)
    // All three cosmetic, as every client-side check on this page is: PUT /reports/{id},
    // /legacy/* and DELETE /reports/{id} are gated on these same keys server-side, so hiding a
    // control decides nothing.
    const canDesign = user?.permissions?.includes('boss.reports.design') ?? false
    const canImport = user?.permissions?.includes('boss.reports.import') ?? false
    const canDeleteReports = user?.permissions?.includes('boss.reports.delete') ?? false
    // The "View SQL" window is SuperAdmin-only. Cosmetic, as every client-side check here is: the
    // endpoint refuses anyone below that rank regardless of what this hides.
    const isSuperAdmin = user?.role === 'SuperAdmin'

    const qc = useQueryClient()
    const [params, setParams] = useSearchParams()
    const [mutationError, setMutationError] = useState<string | null>(null)
    const [pickerOpen, setPickerOpen] = useState(false)
    const [pickerTab, setPickerTab] = useState<ReportPickerTab>('web')
    const [importResult, setImportResult] = useState<BossLegacyImportResult | null>(null)
    // The report the "Permanently Delete Report" confirm dialog is asking about; null when closed.
    // Held by the page rather than by the panel or the picker, because either can raise it and the
    // report it destroys outlives both.
    const [pendingDelete, setPendingDelete] = useState<{ reportId: string; name: string } | null>(null)
    // null = closed. 'openFlow' means the dialog opened itself because the report demands values,
    // and cancelling it aborts the load (the legacy behaviour); 'manual' just closes again.
    const [dialogMode, setDialogMode] = useState<null | 'openFlow' | 'manual' | 'loadWithFilters'>(null)
    const [viewerOverride, setViewerOverride] = useState<Record<string, unknown> | null>(null)
    // Which row fields are frozen and which scroll — the second half of the session layout. It
    // lives beside the viewer config rather than in it because Perspective's save() drops keys it
    // does not know; same session-only rule, same Reset / Save.
    const [pinsOverride, setPinsOverride] = useState<BossRowPins | null>(null)
    // The third half of the session layout: the row field a side-by-side grid is collapsed to.
    // Wrapped, because "no override" and "overridden to fully expanded" are both otherwise null.
    const [collapseOverride, setCollapseOverride] = useState<{ to: string | null } | null>(null)
    // The row fields whose subtotal rows are switched off ("Show Totals"); null = as saved.
    const [totalsOverride, setTotalsOverride] = useState<string[] | null>(null)
    // The report whose opening pre-filter question has been answered. Null while the dialog is
    // still waiting. An answer of "no filters at all" counts — see `ready`.
    const [promptAnsweredFor, setPromptAnsweredFor] = useState<string | null>(null)
    /**
     * A "Load … with filters…" in flight on a report the user was NOT already looking at: the
     * report it switched away from, so Cancel can go back to it. Null while no such load is open,
     * and `from` is empty when nothing was open to return to.
     */
    const [pendingLoad, setPendingLoad] = useState<{ report: string; from: string } | null>(null)
    /**
     * The opening prompt sequence: one dialog per pre-filter field flagged `prompt`, asked in the
     * report's own field order. Null when the report prompts on nothing, or once the last step has
     * been applied.
     *
     * `index` is the step on screen and `answers` accumulates across steps — nothing reaches the
     * URL, and so nothing runs, until the last one is applied. Cancelling any step abandons the
     * whole load, exactly as cancelling the single dialog always did.
     *
     * Distinct from `pendingLoad` above, and they never overlap: this is the report's OWN opening
     * questions ('openFlow'), that one is a "Load … with filters…" the user asked for from the
     * shortcut menu ('loadWithFilters'), which offers every field at once and no sequence.
     */
    const [promptQueue, setPromptQueue] = useState<
        { reportId: string; fields: string[]; index: number; answers: BossPreFilterValue[] } | null>(null)
    // The grid's right-click menu: null when closed, otherwise the hit-tested target PivotGrid
    // reported and the viewport point to open at.
    const [menu, setMenu] = useState<{ target: BossGridTarget; position: { x: number; y: number } } | null>(null)
    // The SuperAdmin "View SQL" window. Opening it is what fetches the preview — nobody pays for
    // a second server round trip per run just in case somebody wants to look at the query.
    const [sqlOpen, setSqlOpen] = useState(false)
    // The viewer adapter's handle, so Expand / Collapse All can drive the live View. Depth is not
    // part of the config and is never saved with the layout (gap analysis C5).
    const viewerRef = useRef<BossViewerHandle | null>(null)

    const reportId = params.get('report') ?? ''
    const promptFlag = params.get('prompt') === '1'
    const myStoreId = params.get('store') ?? ''
    const dialogOpen = dialogMode !== null

    // Copied from AdventureBucksAuditPage: replace-mode so filter tweaks do not fill the history
    // stack. This page has no `page` key, so nothing is reset alongside the change.
    // `null` REMOVES a key; an empty string sets one. The two used to mean the same thing, which
    // made a deliberately-cleared pre-filter (`f_STORENAME=`, §4.3) unrepresentable — the key was
    // dropped, the server read the field as unanswered, and the stored default the user had just
    // removed came back. Every caller that means "remove" passes null explicitly.
    const patch = useCallback((changes: Record<string, string | null>) => {
        setParams(prev => {
            const next = new URLSearchParams(prev)
            for (const [k, v] of Object.entries(changes)) {
                if (v === null) next.delete(k)
                else next.set(k, v)
            }
            return next
        }, { replace: true })
    }, [setParams])

    // ── Queries ───────────────────────────────────────────────────────

    const shortcuts = useQuery<BossShortcutBar>({
        queryKey: ['boss-shortcuts'],
        queryFn: () => api('/api/boss-reports/shortcuts', token),
        enabled: !!token,
        staleTime: 5 * 60_000,
    })

    const reports = useQuery<BossReportList>({
        queryKey: ['boss-reports'],
        queryFn: () => api('/api/boss-reports/reports', token),
        enabled: !!token,
        staleTime: 5 * 60_000,
    })

    // A report definition and its layout are shared by everyone, so this query overrides the app's
    // five-minute default: a layout somebody else saves must reach an open tab, and without a
    // focus refetch it would not arrive until the tab was reloaded. `openFlowRef` keys the
    // pre-filter open-flow on the report id, so a refetch cannot reopen the prompt dialog.
    const detail = useQuery<BossReportDetail>({
        queryKey: ['boss-report', reportId],
        queryFn: () => api(`/api/boss-reports/reports/${reportId}`, token),
        enabled: !!token && !!reportId,
        staleTime: 0,
        refetchOnWindowFocus: true,
    })

    const legacy = useQuery<BossLegacyList>({
        queryKey: ['boss-legacy-reports'],
        queryFn: () => api('/api/boss-reports/legacy/reports', token),
        enabled: pickerOpen && pickerTab === 'legacy' && canImport,
    })

    // What deleting the report under the confirm dialog would cost. Fetched when the dialog opens
    // rather than with the report list: it is three counts per report, and nobody pays for them
    // until somebody actually reaches for Delete.
    const deleteImpact = useQuery<BossDeleteImpact>({
        queryKey: ['boss-delete-impact', pendingDelete?.reportId],
        queryFn: () => api(`/api/boss-reports/reports/${pendingDelete!.reportId}/delete-impact`, token),
        enabled: !!token && !!pendingDelete && canDeleteReports,
    })

    // ── Pre-filter state ──────────────────────────────────────────────

    const fields: BossField[] = useMemo(() => detail.data?.fields ?? [], [detail.data])
    const filterFields = useMemo(
        () => fields.filter(f => f.isFiltered && !f.isOrphaned),
        [fields])

    // URL values win over the report's persisted defaults, wholesale: a link that carries filters
    // describes the run completely, and a link that carries none opens the report as designed.
    //
    // "None" is ambiguous on its own, though — an untouched link and one where the user removed the
    // last filter look identical, and falling back to the defaults would quietly put back the value
    // they just deleted. NO_FILTERS_KEY is how an explicit empty answer says so.
    const hasUrlFilters = useMemo(
        () => params.has(NO_FILTERS_KEY) || Array.from(params.keys()).some(isFilterKey),
        [params])

    /**
     * Decoded against EVERY field, not just the report's own pre-filters: a run may carry a filter
     * on a field the report never marked filterable ("Load with filters"), and decoding against
     * `filterFields` would drop it silently. `decodePreFilters` still refuses a name that is not a
     * field at all, or is orphaned, which is the check that matters.
     */
    const values = useMemo<BossPreFilterValue[]>(
        () => (hasUrlFilters ? decodePreFilters(params, fields) : defaultsFromFields(filterFields)),
        [hasUrlFilters, params, fields, filterFields])

    /**
     * The pre-filter fields this report asks about when it opens — one dialog each, in the report's
     * own field order.
     *
     * `prompt` on a field is the legacy tool's "ask me for this one on open", and it asks even when
     * the field carries a stored default: the default seeds the editor, it does not answer for the
     * user. That only holds for a report opened fresh, though. A link that already carries filter
     * values describes its run completely (§4.2), so there only the prompt fields that link left
     * unanswered are asked — re-asking the rest would overwrite what the sender chose.
     */
    const promptFields = useMemo(
        () => (hasUrlFilters
            ? unresolvedPrompts(filterFields, values)
            : filterFields.filter(f => f.prompt)),
        [hasUrlFilters, filterFields, values])

    const needsMyStore = useMemo(
        () => (detail.data?.report.hasMyStore ?? false)
            || values.some(v => (v.include ?? []).includes('MYSTORE') || (v.exclude ?? []).includes('MYSTORE')),
        [detail.data, values])

    const needsSuppliers = useMemo(
        () => filterFields.some(f => f.filterType === 'supplier'),
        [filterFields])

    const stores = useQuery<BossStorePicker>({
        // Also enabled for the toolbar's MyStore picker: a caller with more than one assigned
        // store must be able to name one, or every MYSTORE report 400s.
        queryKey: ['boss-pickers-stores'],
        queryFn: () => api('/api/boss-reports/pickers/stores', token),
        enabled: !!token && (dialogOpen || needsMyStore),
        staleTime: 30 * 60_000,
    })

    const suppliers = useQuery<BossSupplierPicker>({
        queryKey: ['boss-pickers-suppliers'],
        queryFn: () => api('/api/boss-reports/pickers/suppliers', token),
        enabled: !!token && dialogOpen && needsSuppliers,
        staleTime: 30 * 60_000,
    })

    // The serialised filters are the run's cache key, so the cached result changes exactly when
    // the inputs do. The MyStore id is part of those inputs, so it rides along.
    const filterKey = useMemo(() => {
        const encoded = encodePreFilters(values)
        if (myStoreId) encoded.set('store', myStoreId)
        return encoded.toString()
    }, [values, myStoreId])

    const reportNeedsReview = detail.data?.report.needsReview ?? false

    // A pre-filter is never mandatory. `prompt` on a field means "ask me when the report opens",
    // exactly as it did in the legacy tool, where the editor appeared and OK ran the report with
    // whatever was in it — including nothing. What gates the run is therefore whether that question
    // has been *answered*, not whether an answer narrowed anything: treating an empty answer as
    // unresolved left the report permanently unrunnable, and clearing a value un-ran a report that
    // was already on screen.
    const ready = !!detail.data && !reportNeedsReview && promptAnsweredFor === reportId

    const runBody = useMemo<BossRunRequest>(
        () => (myStoreId ? { filters: values, myStoreId } : { filters: values }),
        [values, myStoreId])

    // Set for the one request Run again triggers, and cleared as that request is built. Anything
    // else — mounting, a refetch React Query decides on, a remount after a tab change — leaves it
    // false and may be answered from the server's cache.
    const forceRefresh = useRef(false)

    const run = useQuery<BossRunResult>({
        queryKey: ['boss-run', reportId, filterKey],
        queryFn: () => {
            const body = forceRefresh.current ? { ...runBody, refresh: true } : runBody
            forceRefresh.current = false
            return apiPost(`/api/boss-reports/reports/${reportId}/run`, token, body)
        },
        enabled: !!token && !!reportId && ready,
        // The server keeps a finished payload for 30 minutes (BossRunCache), so re-asking inside
        // that window costs a round trip and no SQL. Matching the client's window to it means a
        // report reopened in the same session does not even make the round trip; a browser refresh
        // throws this cache away entirely and is caught by the server's.
        staleTime: 30 * 60_000,
        gcTime: 30 * 60_000,
    })

    // Keyed on the same filters as the run, so the window always describes the result on screen
    // rather than whatever was asked for last. Fetched only while the window is open.
    const sqlPreview = useQuery<BossSqlPreview>({
        queryKey: ['boss-report-sql', reportId, filterKey],
        queryFn: () => apiPost(`/api/boss-reports/reports/${reportId}/sql`, token, runBody),
        enabled: !!token && !!reportId && isSuperAdmin && sqlOpen,
        staleTime: 0,
    })

    // ── Open flow ─────────────────────────────────────────────────────

    // The question is asked once per report. Keyed on the report alone, deliberately: applying
    // clears `prompt` from the URL, so a key that included that flag would change on apply and
    // bounce the dialog straight back open.
    const openFlowRef = useRef<string | null>(null)
    useEffect(() => {
        if (!reportId || !detail.data) return
        if (openFlowRef.current === reportId) return
        openFlowRef.current = reportId

        // Ask when the report wants to be asked; otherwise it is already answered and can run.
        if (!promptFlag && promptFields.length === 0) {
            setPromptAnsweredFor(reportId)
            return
        }

        setPromptAnsweredFor(null)

        // One screen per prompted field, as the legacy tool did — it put up the editor for each
        // prompt in turn, and OK on the last one ran the report. The whole-report dialog is the
        // fallback for `?prompt=1` on a report that flags nothing: the URL asked to be asked, and
        // there is no per-field sequence to build.
        setPromptQueue(promptFields.length > 0
            ? {
                reportId,
                fields: promptFields.map(f => f.name),
                index: 0,
                // Seeded with everything NOT being asked about, so the report's own defaults on its
                // other pre-filters survive: applying replaces the URL's filters wholesale, and an
                // answer set built only from the prompts would silently drop the rest.
                answers: values.filter(v => !promptFields.some(f => f.name === v.field)),
            }
            : null)
        setDialogMode('openFlow')
        // `values` and `promptFields` are read once, at the moment the flow opens for this report,
        // and are deliberately absent from the dependencies: both change as each step is applied,
        // and re-running this would restart the sequence half-way through it. `openFlowRef` above
        // makes that safe — the body runs at most once per report id either way.
    }, [reportId, detail.data, promptFlag])

    /**
     * The prompt step on screen: which field is being asked about and where it sits in the run.
     * Null when there is no sequence, or when the queue belongs to a report the URL has since
     * moved off — a stale queue must not put a dialog over a different report.
     */
    const promptStep = useMemo(() => {
        if (!promptQueue || promptQueue.reportId !== reportId) return null
        const name = promptQueue.fields[promptQueue.index]
        const field = fields.find(f => f.name === name)
        if (!field) return null
        return {
            field,
            ordinal: promptQueue.index + 1,
            total: promptQueue.fields.length,
            isLast: promptQueue.index === promptQueue.fields.length - 1,
        }
    }, [promptQueue, reportId, fields])

    /**
     * What the step's editor opens with: the answer already banked for this field if the user has
     * been here before, otherwise the report's own stored default for it. Scoped to the one field,
     * so a step cannot show — or return — a value belonging to another prompt.
     */
    const promptStepValues = useMemo(() => {
        if (!promptStep || !promptQueue) return []
        const name = promptStep.field.name
        const banked = promptQueue.answers.find(v => v.field === name)
        return banked ? [banked] : values.filter(v => v.field === name)
    }, [promptStep, promptQueue, values])

    // A different report means a different layout; drop the in-memory viewer config with it, along
    // with any grid menu or SQL window still open over the report that is going away.
    useEffect(() => {
        setViewerOverride(null); setPinsOverride(null); setCollapseOverride(null); setTotalsOverride(null)
        setMenu(null); setSqlOpen(false)
    }, [reportId])

    // Perspective is themed by name, not by CSS variables, so the page watches the attribute
    // ThemeInitializer writes and hands the grid a plain 'light' | 'dark'.
    const [colorMode, setColorMode] = useState<'light' | 'dark'>('dark')
    useEffect(() => {
        const el = document.documentElement
        const read = () => setColorMode(el.getAttribute('data-color-mode') === 'light' ? 'light' : 'dark')
        read()
        const observer = new MutationObserver(read)
        observer.observe(el, { attributes: true, attributeFilter: ['data-color-mode'] })
        return () => observer.disconnect()
    }, [])

    // ── Cache invalidation helpers ────────────────────────────────────

    const refreshShortcuts = useCallback(() => {
        void qc.invalidateQueries({ queryKey: ['boss-shortcuts'] })
    }, [qc])

    const refreshReports = useCallback(() => {
        void qc.invalidateQueries({ queryKey: ['boss-reports'] })
        if (reportId) void qc.invalidateQueries({ queryKey: ['boss-report', reportId] })
    }, [qc, reportId])

    // ── Mutations ─────────────────────────────────────────────────────

    const shortcutMutation = useMutation({
        mutationFn: (action: ShortcutAction) => {
            if (action.kind === 'addGroup') {
                return apiPost('/api/boss-reports/shortcut-groups', token,
                    { name: action.name, sortOrder: shortcuts.data?.groups.length ?? 0 })
            }
            if (action.kind === 'renameGroup') {
                return apiPut(`/api/boss-reports/shortcut-groups/${action.groupId}`, token,
                    { name: action.name })
            }
            if (action.kind === 'deleteGroup') {
                return apiDelete(`/api/boss-reports/shortcut-groups/${action.groupId}`, token)
            }
            if (action.kind === 'pin') {
                return apiPost('/api/boss-reports/shortcuts', token,
                    { groupId: action.groupId, reportId: action.reportId })
            }
            if (action.kind === 'unpin') {
                return apiDelete(`/api/boss-reports/shortcuts/${action.shortcutId}`, token)
            }
            return apiPut('/api/boss-reports/shortcuts/order', token, { shortcuts: action.shortcuts })
        },
        onSuccess: refreshShortcuts,
        onError: (err: unknown) => { setMutationError(errorMessage(err)); refreshShortcuts() },
    })

    const importMutation = useMutation({
        mutationFn: (vars: { reportGuids: string[]; onConflict: 'skip' | 'overwrite' }) =>
            apiPost('/api/boss-reports/legacy/import', token, vars) as Promise<BossLegacyImportResult>,
        onSuccess: result => {
            setImportResult(result)
            void qc.invalidateQueries({ queryKey: ['boss-legacy-reports'] })
            void qc.invalidateQueries({ queryKey: ['boss-reports'] })
        },
        onError: (err: unknown) => setMutationError(errorMessage(err)),
    })

    /**
     * "Permanently Delete Report" — the DEFINITION, which every user shares, not a shortcut.
     *
     * The shortcut bar is invalidated alongside the report list because the server cascades every
     * user's shortcut to the report away with it: leaving the cached bar alone would draw a
     * shortcut whose report 404s on the next click. If the deleted report is the one on screen,
     * the `report` param goes with it — the page then shows its own "pick a report" state rather
     * than re-running a query against something that no longer exists.
     */
    const deleteReport = useMutation({
        mutationFn: (vars: { reportId: string; name: string }) =>
            apiDelete(`/api/boss-reports/reports/${vars.reportId}`, token),
        onSuccess: (_result, vars) => {
            setPendingDelete(null)
            void qc.invalidateQueries({ queryKey: ['boss-reports'] })
            void qc.invalidateQueries({ queryKey: ['boss-shortcuts'] })
            void qc.invalidateQueries({ queryKey: ['boss-legacy-reports'] })
            qc.removeQueries({ queryKey: ['boss-report', vars.reportId] })
            if (vars.reportId === reportId) patch({ report: null, prompt: null })
        },
        // The picker closes too. `mutationError` renders through InlineError on the PAGE, which is
        // behind the picker's dimming backdrop — a delete raised from the picker that 409s or
        // fails on the network would otherwise close the confirm and show the user nothing.
        onError: (err: unknown) => {
            setPendingDelete(null)
            setPickerOpen(false)
            setMutationError(errorMessage(err))
        },
    })

    const saveDefaults = useMutation({
        mutationFn: (next: BossPreFilterValue[]) => {
            const loaded = detail.data
            if (!loaded) return Promise.resolve(null)
            const byField = new Map(next.map(v => [v.field, v] as const))
            return apiPut(`/api/boss-reports/reports/${reportId}`, token, {
                name: loaded.report.name,
                sql: loaded.sql,
                linkedReportId: loaded.report.linkedReportId,
                isCore: loaded.report.isCore,
                memo: loaded.report.memo,
                fields: loaded.fields.map(f => {
                    const v = byField.get(f.name)
                    return {
                        id: f.id,
                        name: f.name,
                        caption: f.caption,
                        filterColumn: f.filterColumn,
                        dataType: f.dataType,
                        sortOrder: f.sortOrder,
                        isFiltered: f.isFiltered,
                        prompt: f.prompt,
                        // A field the user just gave a value to is one whose value we keep.
                        persistValues: v ? true : f.persistValues,
                        filterType: f.filterType,
                        dateType: v?.dateType ?? f.dateType,
                        seasonCode: v?.seasonCode ?? f.seasonCode,
                        seasonYear: v?.seasonYear ?? f.seasonYear,
                        startDate: v ? anchorDate(v.from) : f.startDate,
                        endDate: v ? anchorDate(v.to) : f.endDate,
                        relativeRange: v?.relativeRange ?? f.relativeRange,
                        reoYears: v?.reoYears ?? f.reoYears,
                        include: v?.include ?? f.include,
                        exclude: v?.exclude ?? f.exclude,
                        isHiddenForStores: f.isHiddenForStores,
                    }
                }),
            })
        },
        onSuccess: refreshReports,
        onError: (err: unknown) => setMutationError(errorMessage(err)),
    })

    // ── Layout persistence ────────────────────────────────────────────

    const canEditReport = detail.data?.canEdit ?? false

    // Rearranging the grid changes THIS SESSION's view and nothing else. It used to write straight
    // back to the report, which meant a stranger's idle drag silently replaced the layout a store
    // had built and kept for years, with no way to get it back. Now the saved layout is the master
    // copy: session changes live in `viewerOverride`, and only an explicit, confirmed Save — by
    // someone the server says may edit the report — writes them over it.
    const handleConfigChange = useCallback((config: Record<string, unknown>) => {
        setViewerOverride(config)
    }, [])

    const layoutDirty = viewerOverride !== null || pinsOverride !== null || collapseOverride !== null
        || totalsOverride !== null
    const [confirmSaveLayout, setConfirmSaveLayout] = useState(false)

    // An arrangement that has not been saved lives only in this tab's memory, so F5, ⌘R, closing
    // the tab or following a link out of the app all destroy it silently. The browser's own
    // "leave site?" prompt is the only thing that can intercept those, and it only appears if a
    // beforeunload handler calls preventDefault — so one is registered exactly while there is
    // something to lose. The wording is the browser's; a page cannot choose it.
    //
    // This covers leaving the APP. React Router's BrowserRouter has no navigation blocker (that
    // needs a data router), so moving to another page within the SPA is not intercepted here.
    useEffect(() => {
        if (!layoutDirty) return

        const warn = (event: BeforeUnloadEvent) => {
            event.preventDefault()
            // Legacy browsers need returnValue set as well as the default prevented.
            event.returnValue = ''
        }

        window.addEventListener('beforeunload', warn)
        return () => window.removeEventListener('beforeunload', warn)
    }, [layoutDirty])

    const saveLayout = useMutation({
        mutationFn: ({ viewer, pins, collapse, hidden }: {
            viewer: Record<string, unknown>; pins: BossRowPins; collapse: string | null; hidden: readonly string[]
        }) =>
            apiPut(`/api/boss-reports/reports/${reportId}/layout`, token, {
                layout: {
                    schemaVersion: 1,
                    viewer,
                    pinnedRowFields: pins.pinned,
                    unpinnedRowFields: pins.unpinned,
                    // Null is "expanded"; the server stores the key only when it names a field.
                    collapseToRowField: collapse,
                    // Empty is "every level's totals shown"; stored only when non-empty.
                    hiddenTotalsRowFields: hidden,
                },
            }),
        onSuccess: (_result, { viewer, pins, collapse, hidden }) => {
            // The saved copy now IS the session copy. Write it into the cached detail FIRST, then
            // drop the overrides: otherwise the grid falls back to the stale saved layout until the
            // re-fetch lands — two redraws, and with Show Totals a visible flash of subtotal rows.
            qc.setQueryData<BossReportDetail>(['boss-report', reportId], old => old && {
                ...old,
                layout: {
                    schemaVersion: 1,
                    viewer,
                    pinnedRowFields: pins.pinned,
                    unpinnedRowFields: pins.unpinned,
                    ...(collapse !== null ? { collapseToRowField: collapse } : {}),
                    ...(hidden.length > 0 ? { hiddenTotalsRowFields: [...hidden] } : {}),
                },
            })
            setViewerOverride(null)
            setPinsOverride(null)
            setCollapseOverride(null)
            setTotalsOverride(null)
            void qc.invalidateQueries({ queryKey: ['boss-report', reportId] })
        },
        onError: (err: unknown) => setMutationError(errorMessage(err)),
    })

    // Back to the master copy. Also the escape hatch for anyone who may not save.
    const resetLayout = useCallback(() => {
        setViewerOverride(null); setPinsOverride(null); setCollapseOverride(null); setTotalsOverride(null)
    }, [])

    // An unsaved layout arrives as `{}` (contract §7.4), so an envelope without a populated
    // `viewer` means "fall back to the default computed from the run result".
    //
    // `withDefaultRowLayout` is why a report imported before the importer wrote group_rollup_mode
    // still opens side by side rather than as Perspective's six-deep indented tree: the stored
    // layout names no row layout, and this supplies the one the legacy grid drew. It only fills a
    // gap — a layout that says 'rollup' was chosen, and is left alone.
    const savedViewer = useMemo<Record<string, unknown> | null>(() => {
        const layout: unknown = detail.data?.layout
        if (!layout || typeof layout !== 'object') return null
        const viewer = (layout as Partial<BossLayout>).viewer
        return viewer && typeof viewer === 'object' && Object.keys(viewer).length > 0
            ? withDefaultRowLayout(viewer as Record<string, unknown>)
            : null
    }, [detail.data])

    const viewerConfig = useMemo<Record<string, unknown>>(
        () => viewerOverride ?? savedViewer ?? (run.data ? defaultViewerConfig(run.data) : {}),
        [viewerOverride, savedViewer, run.data])

    /** The stored pins, if the saved layout carries any; null means "everything pinned". */
    const savedPins = useMemo(() => rowPinsOfEnvelope(detail.data?.layout), [detail.data])

    /**
     * The pins in force, always reconciled against the config actually on screen: a field dragged
     * out of Rows drops out of both lists, one dragged in starts pinned at the right. This is what
     * the grid, the menu and the field bar all read, and what Save writes.
     */
    const rowPins = useMemo(
        () => normaliseRowPins(viewerConfig, pinsOverride ?? savedPins),
        [viewerConfig, pinsOverride, savedPins])

    /** The stored collapse level, if any; null means fully expanded. */
    const savedCollapse = useMemo(
        () => normaliseRowCollapse(viewerConfig, collapseOfEnvelope(detail.data?.layout)),
        [viewerConfig, detail.data])

    /**
     * The level in force, reconciled the same way as the pins: a field dragged out of Rows takes
     * the collapse with it (back to expanded). Only the side-by-side layout honours it — the tree
     * has its own Expand/Collapse All — but the value is kept across a layout switch and back.
     */
    const rowCollapse = useMemo(
        () => normaliseRowCollapse(viewerConfig, collapseOverride ? collapseOverride.to : savedCollapse),
        [viewerConfig, collapseOverride, savedCollapse])

    /** The stored "Show Totals" switches, as the row fields whose subtotals are OFF; empty = all shown. */
    const savedHiddenTotals = useMemo(
        () => normaliseHiddenTotals(viewerConfig, hiddenTotalsOfEnvelope(detail.data?.layout)),
        [viewerConfig, detail.data])

    /** The switches in force, reconciled with the Rows area like the pins and the collapse. */
    const hiddenTotals = useMemo(
        () => normaliseHiddenTotals(viewerConfig, totalsOverride ?? savedHiddenTotals),
        [viewerConfig, totalsOverride, savedHiddenTotals])

    // ── Grid arrangement: field bar and right-click menu ───────────────

    // Every gesture below builds a NEW config with the pure helpers in lib/bossLayoutOps and hands
    // it to `handleConfigChange` — the same session-only path a drag in Perspective's own sidebar
    // takes. None of it touches the server: the note above `handleConfigChange` explains what a
    // save path here would cost, and it costs the same whether the drag happens in the sidebar or
    // in our field bar.

    /** Every field the grid could show, in field-bar order. */
    const gridFields = useMemo(
        () => (run.data ? inventory(viewerConfig, run.data) : []),
        [viewerConfig, run.data])

    /** The Values area — what "Sort By ▸ <measure>" (C7) has to offer. */
    const measures = useMemo(
        () => gridFields.filter(f => f.area === 'columns').map(f => f.name),
        [gridFields])

    /**
     * Expand All opens every row level; Collapse All goes back to 0.
     *
     * Counted from the config the viewer is actually showing, not from `gridFields`: `inventory`
     * drops any entry the run result no longer has, so a layout grouping on a column the report's
     * SQL stopped returning would under-count and Expand All would stop one level short of the
     * tree the grid is drawing.
     */
    const groupDepth = useMemo(
        () => (Array.isArray(viewerConfig.group_by) ? viewerConfig.group_by.length : 0),
        [viewerConfig])

    /**
     * Stable, so PivotGrid's capture-phase `contextmenu` listener is installed once rather than
     * torn down and re-attached on every render of this page — and it re-renders on every query
     * state change and every keystroke in the My Store select.
     */
    const openGridMenu = useCallback(
        (target: BossGridTarget, position: { x: number; y: number }) => setMenu({ target, position }),
        [])

    const moveGridField = useCallback((name: string, area: BossFieldArea, index: number) => {
        // The schema rides along so a date dropped in the Column area becomes the auto-year
        // expression instead of one column per distinct instant (contract §2.1).
        handleConfigChange(moveField(viewerConfig, name, area, index, run.data?.schema))
    }, [handleConfigChange, viewerConfig, run.data])

    const setGridAggregate = useCallback((name: string, aggregate: BossAggregate | null) => {
        handleConfigChange(setAggregate(viewerConfig, name, aggregate))
    }, [handleConfigChange, viewerConfig])

    const closeMenu = useCallback(() => setMenu(null), [])

    /** The column the menu was opened over; null on empty space, or a row header with no field. */
    const menuColumn = menu && menu.target.kind !== 'empty' ? menu.target.column : null

    // Perspective's sort vocabulary differs by area: 'col asc' / 'col desc' order the SPLIT
    // COLUMNS, plain 'asc' / 'desc' order the rows. What tells the two apart here is where the
    // menu was opened, not the column it names — the hit test reports a column header's last
    // non-empty path entry, which under a split is the measure, never the split field. And with
    // no split_by there is nothing for 'col asc' to order, so an unsplit grid sorts its rows
    // however the menu was reached.
    const menuInColumnArea = menu?.target.kind === 'columnHeader'
        && gridFields.some(f => f.area === 'split_by')

    /**
     * The single `sort` entry `setSortBySummary` writes, read back so the menu can tick the current
     * choice. An imported layout can carry a direction this UI never writes ('asc abs', 'none');
     * those read as "no current sort" rather than being ticked as something they are not.
     */
    const sortedBy = useMemo<{ measure: string; direction: 'asc' | 'desc' } | null>(() => {
        const sort: unknown = viewerConfig.sort
        if (!Array.isArray(sort) || sort.length === 0) return null
        const entry: unknown = sort[0]
        if (!Array.isArray(entry)) return null
        const measure: unknown = entry[0]
        const dir: unknown = entry[1]
        if (typeof measure !== 'string' || typeof dir !== 'string') return null
        if (dir.endsWith('desc')) return { measure, direction: 'desc' }
        if (dir.endsWith('asc')) return { measure, direction: 'asc' }
        return null
    }, [viewerConfig])

    // Depth is a property of the live View, not of the layout (gap analysis C5): a config change
    // rebuilds the View fully expanded, the adapter re-applies the remembered depth, and nothing of
    // it is ever persisted. So these two go through the viewer handle, not through the config.
    const expandAll = useCallback(() => {
        viewerRef.current?.setDepth(groupDepth).catch((err: unknown) => {
            console.warn('[BOSS] expand all could not be applied', err)
        })
        setMenu(null)
    }, [groupDepth])

    const collapseAll = useCallback(() => {
        viewerRef.current?.setDepth(0).catch((err: unknown) => {
            console.warn('[BOSS] collapse all could not be applied', err)
        })
        setMenu(null)
    }, [])

    const sortBySummary = useCallback((measure: string | null, direction: 'asc' | 'desc') => {
        const next = setSortBySummary(viewerConfig, measure, direction, menuInColumnArea)
        // Both ops always return a fresh object, so choosing the sort that is already in force
        // would otherwise mark the session dirty and raise Save over a change nobody made.
        if (JSON.stringify(next.sort) !== JSON.stringify(viewerConfig.sort)) handleConfigChange(next)
        setMenu(null)
    }, [handleConfigChange, viewerConfig, menuInColumnArea])

    const formatColumn = useCallback((format: BossFormat) => {
        // S3 is unverified: the same columns_config entry on a group_by field may or may not reach
        // the tree labels. It is written regardless — the menu offers the item wherever the legacy
        // one did, and nothing else in the config depends on the answer.
        if (!menuColumn) return
        const next = setFormat(viewerConfig, menuColumn, format)
        // Same no-op guard as the sort: picking the format already in force is not an edit.
        if (JSON.stringify(next.columns_config) !== JSON.stringify(viewerConfig.columns_config)) {
            handleConfigChange(next)
        }
        setMenu(null)
    }, [handleConfigChange, viewerConfig, menuColumn])

    // ── Panel placement ───────────────────────────────────────────────
    //
    // Each panel is either in the layout or floating over the grid, and either showing or not.
    // Both are remembered per browser (lib/bossPanels). The four booleans below are the only thing
    // the render needs: a panel is drawn in exactly one of the two places, or nowhere.
    const { panels, setVisible: setPanelVisible, setMode: setPanelMode } = useBossPanels()

    const shortcutsDocked = panels.shortcuts.visible && panels.shortcuts.mode === 'docked'
    const shortcutsFloating = panels.shortcuts.visible && panels.shortcuts.mode === 'popup'
    const fieldsDocked = panels.fields.visible && panels.fields.mode === 'docked'
    const fieldsFloating = panels.fields.visible && panels.fields.mode === 'popup'

    const rowLayout = useMemo(() => rowLayoutOf(viewerConfig), [viewerConfig])

    /**
     * Whether the column the menu was opened over is pinned, or null when pinning cannot apply.
     *
     * A derived column (MARGIN/ROI) is excluded because the engine cannot group on an expression
     * over already-aggregated measures — `pinField` would refuse the move, and an item that does
     * nothing is worse than a missing one.
     */
    const menuPinned = useMemo<boolean | null>(() => {
        if (menuColumn === null) return null
        // Only a ROW field can be pinned or unpinned: pinning decides where a row field is drawn,
        // and a measure has no frozen form. A derived column can never be a row field at all.
        const isRowField = rowPins.pinned.includes(menuColumn) || rowPins.unpinned.includes(menuColumn)
        if (!isRowField) return null
        return isRowFieldPinned(rowPins, menuColumn)
    }, [menuColumn, rowPins])

    /** Pin or unpin one row field. Session-only, like every other layout change. */
    const setFieldPinned = useCallback((name: string, pin: boolean) => {
        const next = pin ? pinRowField(rowPins, name) : unpinRowField(rowPins, name)
        // Both ops return the SAME object for a no-op, so identity is the dirty test — a click that
        // changed nothing must not raise Save.
        if (next !== rowPins) setPinsOverride(next)
    }, [rowPins])

    const setColumnPinned = useCallback((pin: boolean) => {
        if (menuColumn) setFieldPinned(menuColumn, pin)
        setMenu(null)
    }, [menuColumn, setFieldPinned])

    /**
     * The level the side-by-side grid is collapsed to, as its own gestures move it: a field closed
     * on every group, or null once everything is open. The viewer announces it after the groups have
     * moved, so this only records it — session-only, written by Save as `collapseToRowField`. The
     * level the saved layout already has drops the override rather than keeping an equal one, so
     * Save is not raised over nothing.
     */
    const collapseRowsTo = useCallback((field: string | null) => {
        if (field !== rowCollapse) setCollapseOverride(field === savedCollapse ? null : { to: field })
        setMenu(null)
    }, [rowCollapse, savedCollapse])

    /** Expand All / Collapse All on a row field, side by side: every visible group of that field. */
    const openFieldGroups = useCallback((field: string, open: boolean) => {
        viewerRef.current?.setFieldOpen(field, open).catch((err: unknown) => {
            console.warn('[BOSS] the row field could not be opened or closed', err)
        })
        setMenu(null)
    }, [])

    /**
     * Whether the menu's row field has its subtotal rows shown, or null when it can have none: the
     * grid is not collapsed, or the field is the last one, whose groups have nothing below them. A
     * field at or right of the level has subtotal rows once one of its groups is opened with [+].
     */
    const menuTotalsShown = useMemo<boolean | null>(() => {
        if (menuColumn === null || rowCollapse === null) return null
        const rowFields = Array.isArray(viewerConfig.group_by) ? viewerConfig.group_by as unknown[] : []
        const at = rowFields.indexOf(menuColumn)
        if (at < 0 || at >= rowFields.length - 1) return null
        return !hiddenTotals.includes(menuColumn)
    }, [menuColumn, rowCollapse, viewerConfig, hiddenTotals])

    /** Switch one row field's subtotal rows on or off. Session-only; equal to saved drops the override. */
    const setColumnTotals = useCallback((shown: boolean) => {
        if (menuColumn) {
            const next = normaliseHiddenTotals(viewerConfig,
                shown ? hiddenTotals.filter(name => name !== menuColumn) : [...hiddenTotals, menuColumn])
            if (!sameHiddenTotals(next, hiddenTotals)) {
                setTotalsOverride(sameHiddenTotals(next, savedHiddenTotals) ? null : next)
            }
        }
        setMenu(null)
    }, [menuColumn, viewerConfig, hiddenTotals, savedHiddenTotals])

    const changeRowLayout = useCallback((layout: BossRowLayout) => {
        // Same no-op guard as the sort and the format: choosing the layout already in force is
        // not an edit, and marking the session dirty would raise Save over nothing.
        if (layout !== rowLayout) handleConfigChange(setRowLayout(viewerConfig, layout))
        setMenu(null)
    }, [handleConfigChange, viewerConfig, rowLayout])

    // ── Actions ───────────────────────────────────────────────────────

    /** Clears every pre-filter key currently in the URL, so a new report starts clean. */
    const clearedFilterKeys = useCallback(() => {
        const changes: Record<string, string | null> = { [NO_FILTERS_KEY]: null }
        for (const key of Array.from(params.keys())) if (isFilterKey(key)) changes[key] = null
        return changes
    }, [params])

    const selectReport = useCallback((id: string) => {
        patch({ ...clearedFilterKeys(), report: id, prompt: null, store: null })
        setPickerOpen(false)
        setMutationError(null)
    }, [patch, clearedFilterKeys])

    const applyFilters = useCallback((next: BossPreFilterValue[], save: boolean) => {
        const changes: Record<string, string | null> = { ...clearedFilterKeys(), prompt: null }
        let any = false
        for (const [key, value] of encodePreFilters(next).entries()) { changes[key] = value; any = true }
        // Record an empty answer explicitly, or the next render treats the URL as untouched and
        // restores the report's stored defaults over the top of it.
        if (!any) changes[NO_FILTERS_KEY] = '1'
        patch(changes)
        setDialogMode(null)
        // Answered — whatever the answer was. Applying an empty set is a legitimate "no filters",
        // and the report runs unfiltered rather than sitting on the "set your pre-filters" notice.
        if (reportId) setPromptAnsweredFor(reportId)
        setPendingLoad(null)
        if (save) saveDefaults.mutate(next)
    }, [patch, clearedFilterKeys, saveDefaults, reportId])

    /**
     * One step of the opening prompt sequence: bank this field's answer and move to the next
     * prompt, or — on the last one — apply every answer at once and let the report run.
     *
     * An EMPTY answer is a real answer. The dialog returns a value only for a field the user put
     * something in, so skipping a prompt simply contributes nothing and the field goes unfiltered;
     * that is what the legacy editor did when you pressed OK on an empty one, and it is why the
     * step's Skip button exists rather than a disabled Next.
     */
    const applyPromptStep = useCallback((stepValues: BossPreFilterValue[]) => {
        setPromptQueue(queue => {
            if (!queue) return null

            // This step's field replaces whatever was banked for it; every other answer stands.
            const asked = queue.fields[queue.index]
            const answers = [
                ...queue.answers.filter(a => a.field !== asked),
                ...stepValues.filter(v => v.field === asked),
            ]

            const next = queue.index + 1
            if (next < queue.fields.length) return { ...queue, index: next, answers }

            // Last prompt. Deferred out of the updater — React may call it twice in StrictMode,
            // and applying the filters twice would push two history entries and two runs.
            setPendingPromptAnswers(answers)
            return null
        })
    }, [])

    // The completed answer set, held for one tick between the last prompt step and the effect that
    // applies it. It exists only so that the commit happens outside a state updater.
    const [pendingPromptAnswers, setPendingPromptAnswers] = useState<BossPreFilterValue[] | null>(null)
    useEffect(() => {
        if (!pendingPromptAnswers) return
        setPendingPromptAnswers(null)
        applyFilters(pendingPromptAnswers, false)
    }, [pendingPromptAnswers, applyFilters])

    /**
     * The shortcut menu's "Load […] with filters…": open the report, then ask for its filters before
     * anything runs. Any field of it can be filtered, and the answer is not written back to the
     * report — see the dialog's picker.
     */
    const loadWithFilters = useCallback((id: string) => {
        setDialogMode('loadWithFilters')
        // Already looking at it: there is nothing to load and nothing to go back to, so the dialog
        // just edits the run on screen and Cancel leaves it — and its filters — alone.
        if (id === reportId) return

        // Switching. The report must NOT run behind the dialog: "Load Report" is the button that
        // loads it, so Cancel has to leave the user where they were rather than undoing a report
        // that has already fetched. `promptAnsweredFor` is the gate the run waits on, and the
        // open-flow effect is claimed for this report so it cannot answer the gate itself.
        setPendingLoad({ report: id, from: reportId })
        setPromptAnsweredFor(null)
        openFlowRef.current = id
        patch({ report: id, ...clearedFilterKeys(), prompt: null })
    }, [reportId, patch, clearedFilterKeys])

    const cancelFilters = useCallback(() => {
        const mode = dialogMode
        setDialogMode(null)
        // Whichever dialog this was, the prompt sequence goes with it: its banked answers were
        // never applied, so there is nothing to keep and nothing left to commit.
        setPromptQueue(null)
        setPendingPromptAnswers(null)
        // Cancelling the dialog the report opened with aborts the load, as the legacy tool did —
        // and that is true of ANY step of the prompt sequence, not just the first.
        if (mode === 'openFlow') {
            patch({ ...clearedFilterKeys(), report: null, prompt: null, store: null })
            return
        }
        // Cancelling a "Load … with filters…" that switched reports goes back to the one that was
        // open, and the report it was about never ran — the gate above saw to that. The open-flow
        // claim is released with it, or selecting that report later would find its question already
        // asked and wait for an answer that never comes.
        if (mode === 'loadWithFilters' && pendingLoad) {
            openFlowRef.current = null
            setPendingLoad(null)
            patch({ ...clearedFilterKeys(), report: pendingLoad.from || null, prompt: null, store: null })
            return
        }
        // Anything else the user opened themselves just leaves the current result alone.
    }, [dialogMode, pendingLoad, patch, clearedFilterKeys])

    const reorderShortcuts = useCallback((groups: BossShortcutGroup[], order: ShortcutOrderItem[]) => {
        qc.setQueryData<BossShortcutBar>(['boss-shortcuts'], { groups })
        shortcutMutation.mutate({ kind: 'reorder', shortcuts: order })
    }, [qc, shortcutMutation])

    // ── Render ────────────────────────────────────────────────────────

    const shortcutGroups = shortcuts.data?.groups ?? []

    /**
     * Built once and handed to whichever `BossPanel` is on screen.
     *
     * The panel moves between two places in the tree, and React unmounts and remounts a subtree
     * that moves — so this exists to keep the CONTENT's element identity stable across the move
     * where React can preserve it, and, more importantly, to keep one definition of the props.
     * Two copies of this call would drift the moment one of the mutations changed.
     */
    const shortcutPanel = (
        <ShortcutPanel
            groups={shortcutGroups}
            selectedReportId={reportId}
            loading={shortcuts.isLoading}
            error={shortcuts.error ? errorMessage(shortcuts.error) : null}
            busy={shortcutMutation.isPending}
            onSelect={selectReport}
            onLoadWithFilters={loadWithFilters}
            onReorder={reorderShortcuts}
            onAddGroup={name => shortcutMutation.mutate({ kind: 'addGroup', name })}
            onRenameGroup={(groupId, name) => shortcutMutation.mutate({ kind: 'renameGroup', groupId, name })}
            onDeleteGroup={groupId => shortcutMutation.mutate({ kind: 'deleteGroup', groupId })}
            onRemoveShortcut={shortcutId => shortcutMutation.mutate({ kind: 'unpin', shortcutId })}
            canDeleteReports={canDeleteReports}
            onDeleteReport={(id, name) => setPendingDelete({ reportId: id, name })}
            onBrowse={() => { setImportResult(null); setPickerOpen(true) }}
        />
    )

    const fieldBar = (
        <FieldBar
            fields={gridFields}
            rowPins={rowPins}
            onMove={moveGridField}
            onAggregate={setGridAggregate}
            onPin={setFieldPinned}
            busy={run.isFetching}
        />
    )

    /** How the Rows area is drawn, for the menu's tick. */
    const report = detail.data?.report ?? null
    const fieldByName = useMemo(() => new Map(fields.map(f => [f.name, f] as const)), [fields])

    const chips: { key: string; text: string }[] = run.data
        ? run.data.appliedFilters.map(f => ({ key: f.field, text: `${f.field}: ${f.description}` }))
        : values.flatMap(v => {
            const field = fieldByName.get(v.field)
            return field ? [{ key: v.field, text: `${field.caption ?? field.name}: ${describePreFilter(field, v)}` }] : []
        })

    const detailError = detail.error ? errorMessage(detail.error) : null
    const runError = run.error ? errorMessage(run.error) : null

    return (
        <div className="page boss-page">
            <div className="page-header">
                <h1>🧮 BOSS Reports</h1>
                <p>Pivot-grid reporting ported from the legacy BOSS Reporting desktop tool.</p>
            </div>

            {mutationError && (
                <InlineError message={mutationError} onDismiss={() => setMutationError(null)} />
            )}

            <div className={`boss-layout${shortcutsDocked ? '' : ' boss-layout-wide'}`}>
                {shortcutsDocked && (
                    <BossPanel
                        title="Report shortcuts"
                        variant="shortcuts"
                        mode="docked"
                        onMode={mode => setPanelMode('shortcuts', mode)}
                        onClose={() => setPanelVisible('shortcuts', false)}
                    >
                        {shortcutPanel}
                    </BossPanel>
                )}

                <section className="boss-report">
                    <div className="boss-toolbar">
                        <div className="boss-toolbar-title">
                            {report ? report.name : 'No report selected'}
                        </div>
                        <div className="boss-toolbar-actions">
                            {needsMyStore && (
                                <label className="boss-mystore">
                                    My store
                                    <select
                                        className="form-select"
                                        value={myStoreId}
                                        // The blank option means "no store chosen", which is the
                                        // key's absence — `patch` only removes on null now.
                                        onChange={e => patch({ store: e.target.value || null })}
                                    >
                                        <option value="">Use my assigned store</option>
                                        {(stores.data?.stores ?? []).map(s => (
                                            <option key={s.storeId} value={s.storeId}>
                                                {s.storeNumber ? `${s.storeNumber} — ` : ''}{s.storeName}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            )}
                            {/* Show/hide, not dock/undock — where a panel sits is its own control,
                                in its header. These stay next to the report's title so the panels
                                can always be got back, whichever state they were left in. */}
                            <button
                                type="button"
                                className="btn btn-secondary boss-btn-sm"
                                aria-pressed={panels.shortcuts.visible}
                                title={panels.shortcuts.visible ? 'Hide the shortcut panel' : 'Show the shortcut panel'}
                                onClick={() => setPanelVisible('shortcuts', !panels.shortcuts.visible)}
                            >☆ Shortcuts</button>
                            <button
                                type="button"
                                className="btn btn-secondary boss-btn-sm"
                                aria-pressed={panels.fields.visible}
                                disabled={!run.data}
                                title={panels.fields.visible ? 'Hide the field editor' : 'Show the field editor'}
                                onClick={() => setPanelVisible('fields', !panels.fields.visible)}
                            >⊞ Fields</button>
                            {layoutDirty && (
                                <button
                                    type="button"
                                    className="btn btn-secondary boss-btn-sm"
                                    title="Discard this session's layout changes and go back to the report's saved layout"
                                    onClick={resetLayout}
                                >↩ Reset layout</button>
                            )}
                            {layoutDirty && canEditReport && (
                                <button
                                    type="button"
                                    className="btn btn-secondary boss-btn-sm"
                                    disabled={saveLayout.isPending}
                                    title="Save this arrangement as the report's layout for everyone"
                                    onClick={() => setConfirmSaveLayout(true)}
                                >💾 Save layout</button>
                            )}
                            {/* Enabled for any report with fields, not just one whose author declared
                                a pre-filter: the dialog's picker can filter any column of it for the
                                run in hand, so gating on `filterFields` left that reachable only by
                                right-clicking a shortcut. */}
                            <button
                                type="button"
                                className="btn btn-secondary boss-btn-sm"
                                disabled={!reportId || fields.length === 0}
                                onClick={() => setDialogMode('manual')}
                            >⚙ Pre-filters</button>
                            <button
                                type="button"
                                className="btn btn-secondary boss-btn-sm"
                                disabled={!ready || run.isFetching}
                                title="Re-run this report against the database, ignoring the last half hour's figures"
                                onClick={() => { forceRefresh.current = true; void run.refetch() }}
                            >↻ Run again</button>
                            {isSuperAdmin && (
                                <button
                                    type="button"
                                    className="btn btn-secondary boss-btn-sm"
                                    disabled={!reportId}
                                    title="Show the report's stored query and the one these pre-filters produce"
                                    onClick={() => setSqlOpen(true)}
                                >🐛 View SQL</button>
                            )}
                            <button
                                type="button"
                                className="btn btn-primary boss-btn-sm"
                                onClick={() => { setImportResult(null); setPickerOpen(true) }}
                            >📂 Reports</button>
                        </div>
                    </div>

                    <div className="boss-prefilters" aria-label="Pre-filters">
                        {reportNeedsReview && (
                            <span className="boss-chip warn">
                                {report?.needsReviewReason
                                    ?? 'This report needs a designer’s attention before it can run.'}
                            </span>
                        )}
                        {!reportId && <span className="boss-muted">Pick a report to get started.</span>}
                        {reportId && chips.length === 0 && !reportNeedsReview && (
                            <span className="boss-muted">No pre-filters — this report runs over everything.</span>
                        )}
                        {chips.map(c => (
                            <span key={c.key} className="boss-chip" title={c.text}>{c.text}</span>
                        ))}
                        {run.data && (
                            <span className="boss-run">
                                <span>{countFmt.format(run.data.rowCount)} rows</span>
                                <span>{countFmt.format(run.data.durationMs)} ms</span>
                                {/*
                                    The figures can be up to half an hour old, so say when they
                                    came out of the database. `ranAt` is the ORIGINAL run's time
                                    even on a replay, which is the whole point of it: without this
                                    nothing on screen distinguishes a cached report from a live one.
                                */}
                                <span className="boss-asof" title={asOfTitle(run.data.ranAt)}>
                                    as of {asOfClock(run.data.ranAt)}
                                </span>
                            </span>
                        )}
                    </div>

                    {detailError && <div className="boss-error">{detailError}</div>}
                    {runError && <div className="boss-error">{runError}</div>}

                    <div className="boss-grid">
                        {/* The float layer lives INSIDE the grid pane, which is what keeps the
                            report's title and buttons visible: it starts below the toolbar, so a
                            floating panel has no way to cover them. It is inert except where a
                            panel actually is, or it would swallow every click meant for the grid. */}
                        {(shortcutsFloating || fieldsFloating) && (
                            <div className="boss-float-layer">
                                {shortcutsFloating && (
                                    <BossPanel
                                        title="Report shortcuts"
                                        variant="shortcuts"
                                        mode="popup"
                                        onMode={mode => setPanelMode('shortcuts', mode)}
                                        onClose={() => setPanelVisible('shortcuts', false)}
                                    >
                                        {shortcutPanel}
                                    </BossPanel>
                                )}
                                {fieldsFloating && run.data && detail.data && (
                                    <BossPanel
                                        title="Fields"
                                        variant="fields"
                                        mode="popup"
                                        onMode={mode => setPanelMode('fields', mode)}
                                        onClose={() => setPanelVisible('fields', false)}
                                    >
                                        {fieldBar}
                                    </BossPanel>
                                )}
                            </div>
                        )}
                        {!reportId && (
                            <div className="boss-empty">
                                Choose a report from the shortcut panel, or browse every report you can see.
                            </div>
                        )}
                        {reportId && detail.isLoading && <div className="boss-muted">Loading the report…</div>}
                        {reportId && !detail.isLoading && !ready && !detailError && !reportNeedsReview && (
                            <div className="boss-empty">Set this report’s pre-filters to run it.</div>
                        )}
                        {reportId && ready && run.isFetching && !run.data && (
                            <div className="boss-muted">Running the report…</div>
                        )}
                        {/* The report's own saved layout must be in hand before the grid mounts.
                            The run and the detail are separate queries and the run often wins;
                            mounting on the fallback config would show the report flat and — because
                            the viewer echoes its config back on load — save that flatness over the
                            arrangement the report was imported with. */}
                        {run.data && detail.data && (
                            <Suspense fallback={<div className="boss-muted">Loading the pivot grid…</div>}>
                                <div className="boss-grid-host">
                                    <PivotGrid
                                        ref={viewerRef}
                                        result={run.data}
                                        config={viewerConfig}
                                        onConfigChange={handleConfigChange}
                                        colorMode={colorMode}
                                        rowPins={rowPins}
                                        rowCollapse={rowCollapse}
                                        hiddenTotals={hiddenTotals}
                                        onCollapseTo={collapseRowsTo}
                                        // The bar goes ABOVE the grid rather than into its headers
                                        // (gap analysis C1): Perspective's headers cannot be made
                                        // drag sources, so this is as close as the engine allows.
                                        // Passing it through the grid keeps it on the grid's own
                                        // width instead of the page's.
                                        toolbar={fieldsDocked && (
                                            <BossPanel
                                                title="Fields"
                                                variant="fields"
                                                mode="docked"
                                                onMode={mode => setPanelMode('fields', mode)}
                                                onClose={() => setPanelVisible('fields', false)}
                                            >
                                                {fieldBar}
                                            </BossPanel>
                                        )}
                                        onContextMenu={openGridMenu}
                                    />
                                </div>
                            </Suspense>
                        )}
                    </div>
                </section>
            </div>

            {pickerOpen && (
                <ReportPicker
                    tab={pickerTab}
                    onTab={setPickerTab}
                    canImport={canImport}
                    canDelete={canDeleteReports}
                    reports={reports.data?.reports ?? []}
                    reportsLoading={reports.isLoading}
                    reportsError={reports.error ? errorMessage(reports.error) : null}
                    legacyGroups={legacy.data?.groups ?? []}
                    legacyLoading={legacy.isLoading}
                    legacyError={legacy.error ? errorMessage(legacy.error) : null}
                    importing={importMutation.isPending}
                    importResult={importResult}
                    shortcutGroups={shortcutGroups}
                    selectedReportId={reportId}
                    onOpenReport={selectReport}
                    onPin={(groupId, id) => shortcutMutation.mutate({ kind: 'pin', groupId, reportId: id })}
                    onImport={(reportGuids, onConflict) => importMutation.mutate({ reportGuids, onConflict })}
                    onDeleteReport={(id, name) => setPendingDelete({ reportId: id, name })}
                    onClose={() => setPickerOpen(false)}
                />
            )}

            {/* The opening prompt sequence: one dialog per field flagged `prompt`, keyed on the
                field so each step mounts fresh rather than inheriting the previous step's drafts.
                Only that field is editable and there is no picker — a prompt asks one question,
                and offering the report's other columns here would turn it into the full editor. */}
            {dialogOpen && detail.data && promptStep && (
                <PreFilterDialog
                    key={promptStep.field.name}
                    fields={[promptStep.field]}
                    values={promptStepValues}
                    stores={stores.data?.stores ?? []}
                    suppliers={suppliers.data?.suppliers ?? []}
                    canSaveDefaults={false}
                    title={promptStep.field.caption || promptStep.field.name}
                    caption={
                        `${promptStep.ordinal} of ${promptStep.total} — this report asks for `
                        + `${promptStep.total === 1 ? 'this filter' : 'these filters'} before it runs. `
                        + 'Leave it empty to run unfiltered on this field. '
                        + 'Cancel stops the report from opening.'}
                    applyLabel={promptStep.isLast ? 'Load Report' : 'Next'}
                    today={detail.data.businessDate}
                    businessTimeZone={detail.data.businessTimeZone}
                    onApply={applyPromptStep}
                    onCancel={cancelFilters}
                />
            )}

            {dialogOpen && detail.data && !promptStep && (
                <PreFilterDialog
                    fields={filterFields}
                    addable={fields}
                    values={values}
                    stores={stores.data?.stores ?? []}
                    suppliers={suppliers.data?.suppliers ?? []}
                    // Never from the shortcut menu: what is chosen there is for that one run, and
                    // an added field is not saved by this button anyway — offering it would promise
                    // something it does not do.
                    // canDesign, not canEditReport: "save as defaults" writes the DEFINITION
                    // through PUT /reports/{id}, which is on the design policy — while canEdit is
                    // the wider owner-or-store-assignee-or-designer rule that governs the LAYOUT.
                    // Offering the button on the wider rule showed a store-assigned viewer a
                    // button the server answers with a 403 (AGENTS.md #4).
                    canSaveDefaults={canDesign && dialogMode !== 'loadWithFilters'}
                    applyLabel={dialogMode === 'loadWithFilters' ? 'Load Report' : undefined}
                    // The server's business date, so the window the dialog previews for a relative
                    // range is the window the run will read. The detail query has no staleTime, so
                    // a page left open past midnight picks up the new date on the next focus.
                    today={detail.data.businessDate}
                    businessTimeZone={detail.data.businessTimeZone}
                    onApply={applyFilters}
                    onCancel={cancelFilters}
                />
            )}
            {/* The grid's right-click menu. It portals itself to a viewport coordinate, so where it
                sits in this tree is immaterial; it lives with the other overlays. */}
            {/* Gated on the grid being mounted, not just on `menu`: a pre-filter apply or a My
                Store change clears `run.data`, which unmounts PivotGrid and nulls the viewer
                handle. A menu left open over the empty pane would act on a grid that is gone. */}
            {menu && run.data && detail.data && (
                <GridMenu
                    target={menu.target}
                    position={menu.position}
                    measures={measures}
                    grouped={isGrouped(viewerConfig)}
                    format={menuColumn ? formatOf(viewerConfig, menuColumn) : 'auto'}
                    sortedBy={sortedBy}
                    rowLayout={rowLayout}
                    pinned={menuPinned}
                    // A pin verdict exists exactly when the target is a row field, which is also
                    // exactly when it has groups to open and close.
                    collapsible={menuPinned !== null}
                    onExpandAll={expandAll}
                    onCollapseAll={collapseAll}
                    onSortBy={sortBySummary}
                    onFormat={formatColumn}
                    onRowLayout={changeRowLayout}
                    onPin={setColumnPinned}
                    onFieldAll={openFieldGroups}
                    totalsShown={menuTotalsShown}
                    onShowTotals={setColumnTotals}
                    onClose={closeMenu}
                />
            )}
            {/* Gated on the rank as well as the flag, so flipping `sqlOpen` in a devtools session
                cannot mount a window whose query the server would refuse anyway. */}
            {sqlOpen && isSuperAdmin && (
                <SqlDebugDialog
                    reportName={report?.name ?? 'this report'}
                    preview={sqlPreview.data ?? null}
                    loading={sqlPreview.isPending}
                    error={sqlPreview.error ? errorMessage(sqlPreview.error) : null}
                    onClose={() => setSqlOpen(false)}
                />
            )}
            {confirmSaveLayout && layoutDirty && report && (
                <ConfirmDialog
                    title="Save layout"
                    message={<>
                        Replace the saved layout of <strong>“{report.name}”</strong> with this
                        arrangement? Everyone who opens the report will see it this way from now on.
                        The current saved layout is not kept.
                    </>}
                    confirmLabel="Save layout"
                    onConfirm={() => {
                        // A pin-only change has no viewer override; the config on screen is the
                        // saved one, and it is re-sent so the envelope stays whole.
                        saveLayout.mutate({
                            viewer: viewerOverride ?? viewerConfig, pins: rowPins, collapse: rowCollapse, hidden: hiddenTotals,
                        })
                        setConfirmSaveLayout(false)
                    }}
                    onCancel={() => setConfirmSaveLayout(false)}
                />
            )}

            {/* Permanently Delete Report. The counts come from the server rather than from
                anything on screen — a report definition is shared, so the cost is other people's
                and this page has no way to know it. The dialog opens before they land and says
                so; it never invents a number, and Delete stays disabled until it can name one.
                Dependents above zero is the 409 the server would return, stated here instead. */}
            {pendingDelete && (
                <ConfirmDialog
                    title="Permanently delete report"
                    danger
                    message={<>
                        Permanently delete <strong>“{pendingDelete.name}”</strong>?
                        {deleteImpact.isLoading && <> Checking what else this affects…</>}
                        {deleteImpact.error && <> The impact could not be read: {errorMessage(deleteImpact.error)}</>}
                        {deleteImpact.data && (deleteImpact.data.dependents > 0 ? (
                            <> {deleteImpact.data.dependents} other report(s) inherit their query from
                            it, so it cannot be deleted until those are changed or removed.</>
                        ) : (
                            <>
                                {' '}This removes the definition for <strong>everyone</strong>, not just
                                you{deleteImpact.data.pinnedByUsers > 0 && <>
                                    {' '}— it is pinned in {deleteImpact.data.pinnedByUsers}
                                    {/* "1 user’s shortcut panel" / "3 users’ shortcut panels": the
                                        apostrophe moves with the plural, so the two halves cannot
                                        be written as one suffix. */}
                                    {deleteImpact.data.pinnedByUsers === 1
                                        ? ' user’s shortcut panel'
                                        : ' users’ shortcut panels'}
                                    , and those shortcuts go with it</>}.
                                {' '}Its saved layout and pre-filters are lost and cannot be recovered;
                                {deleteImpact.data.runs > 0
                                    ? <> its {deleteImpact.data.runs} logged run(s) are kept in the audit trail.</>
                                    : <> the run audit trail is kept.</>}
                            </>
                        ))}
                    </>}
                    confirmLabel={deleteReport.isPending ? 'Deleting…' : 'Delete permanently'}
                    confirmDisabled={!deleteImpact.data || deleteImpact.data.dependents > 0 || deleteReport.isPending}
                    onConfirm={() => {
                        // Guarded, not just styled: the dialog can be confirmed before the impact
                        // lands, and a report with dependents is a 409 the user need not provoke.
                        if (!deleteImpact.data || deleteImpact.data.dependents > 0) return
                        if (deleteReport.isPending) return
                        deleteReport.mutate(pendingDelete)
                    }}
                    onCancel={() => setPendingDelete(null)}
                />
            )}
        </div>
    )
}
